import React, { useEffect, useState, useCallback } from 'react';
import { matchesAPI } from '../../services/api';
import { getTeamLogoPath } from '../../utils/teams';
import { formatMatchTime } from '../../utils/matchHelpers';
import './TeamMatchupsModal.css';

const TeamMatchupsModal = ({ team, onClose }) => {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    matchesAPI.getTeamMatches(team.id)
      .then(res => {
        if (!cancelled) setMatches(res.data.matches);
      })
      .catch(() => {
        if (!cancelled) setError('Failed to load matches.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [team.id]);

  const handleBackdropClick = useCallback((e) => {
    if (e.target === e.currentTarget) onClose();
  }, [onClose]);

  return (
    <div className="team-matchups-backdrop" onClick={handleBackdropClick}>
      <div className="team-matchups-modal">
        <div className="team-matchups-header">
          <img
            src={getTeamLogoPath(team.id)}
            alt={team.name}
            className="team-matchups-logo"
          />
          <span className="team-matchups-title">
            {team.name} — {matches.length > 0 ? new Date(matches[0].scheduledTime).getFullYear() : '…'} Season
          </span>
          <button className="team-matchups-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="team-matchups-body">
          {loading && <div className="team-matchups-status">Loading...</div>}
          {error && <div className="team-matchups-status team-matchups-error">{error}</div>}
          {!loading && !error && matches.length === 0 && (
            <div className="team-matchups-status">No matches found.</div>
          )}
          {!loading && !error && matches.map(match => {
            const isHome = match.homeTeam.id === team.id;
            const opponent = isHome ? match.awayTeam : match.homeTeam;
            const teamScore = isHome ? match.homeScore : match.awayScore;
            const oppScore = isHome ? match.awayScore : match.homeScore;
            const played = ['completed', 'awaiting_scores'].includes(match.status);
            const isWin = played && match.winnerTeamId === team.id;
            const isLoss = played && match.winnerTeamId && match.winnerTeamId !== team.id;
            const isDraw = played && !match.winnerTeamId && teamScore !== null;

            let resultClass = '';
            let resultLabel = '';
            if (isWin) { resultClass = 'result-win'; resultLabel = 'W'; }
            else if (isLoss) { resultClass = 'result-loss'; resultLabel = 'L'; }
            else if (isDraw) { resultClass = 'result-draw'; resultLabel = 'D'; }

            return (
              <div key={match.id} className={`team-matchup-row ${resultClass}`}>
                <span className="matchup-round">R{match.roundNumber}</span>
                <span className="matchup-venue-tag">{isHome ? 'H' : 'A'}</span>
                <img
                  src={getTeamLogoPath(opponent.id)}
                  alt={opponent.name}
                  className="matchup-opp-logo"
                />
                <span className="matchup-opp-name">{opponent.name}</span>
                <span className="matchup-date">{formatMatchTime(match.scheduledTime)}</span>
                {played && teamScore !== null ? (
                  <span className={`matchup-score ${resultClass}`}>
                    {resultLabel} {teamScore}–{oppScore}
                  </span>
                ) : (
                  <span className="matchup-score matchup-score-pending">—</span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TeamMatchupsModal;
